import React, { useMemo } from 'react'

import InfiniteScroll from 'react-infinite-scroller'

import Loading from '@web/components/Loading'
import { UserRelationshipItem_UserFragment, UserRelationshipType } from '@web/gql/graphql'
import usePageInfo from '@web/hooks/usePageInfo'
import nClicks from '@web/libs/nlog/click/codes'

import UserRelationshipItem from './UserRelationshipItem'
import UserRelationshipMoreButton from './UserRelationshipMoreButton'

interface Props {
  type: UserRelationshipType
  users: UserRelationshipItem_UserFragment[]
  loading: boolean
  fetchMore: () => void
  totalUsers: number
  useInfiniteScroll?: boolean
  hasMoreCursor: boolean
}
const myPageCodeMap = {
  [UserRelationshipType.Following]: nClicks.followListMy.FOLLOWINGMORE,
  [UserRelationshipType.Follower]: nClicks.followListMy.FOLLOWERMORE,
  [UserRelationshipType.FollowRequest]: nClicks.followListMy.REQMORE,
  [UserRelationshipType.FollowRequester]: nClicks.followListMy.REQMORE,
  [UserRelationshipType.Block]: nClicks.followListMy.BLOCKMORE,
}

const otherPageCodeMap = {
  [UserRelationshipType.Following]: nClicks.followListYour.FOLLOWINGMORE,
  [UserRelationshipType.Follower]: nClicks.followListYour.FOLLOWERMORE,
  [UserRelationshipType.FollowRequest]: undefined,
  [UserRelationshipType.FollowRequester]: undefined,
  [UserRelationshipType.Block]: undefined,
}

const UserRelationshipList = ({
  users,
  type,
  useInfiniteScroll = false,
  loading,
  totalUsers,
  fetchMore,
  hasMoreCursor,
}: Props) => {
  const { isMyPage } = usePageInfo()

  const remains = totalUsers - users.length

  const moreButtonClickCode = useMemo(() => (isMyPage ? myPageCodeMap[type] : otherPageCodeMap[type]), [type, isMyPage])

  return (
    <>
      {useInfiniteScroll ? (
        <InfiniteScroll
          element="ul"
          hasMore={!loading && hasMoreCursor}
          loadMore={fetchMore}
          loader={<Loading height={250} key="loading" />}
          threshold={50}
        >
          {users.map(user => (
            <UserRelationshipItem user={user} key={user.id} type={type} />
          ))}
        </InfiniteScroll>
      ) : (
        <>
          <ul>
            {users.map(user => (
              <UserRelationshipItem user={user} key={user.id} type={type} />
            ))}
          </ul>
          {loading ? (
            <Loading height={250} />
          ) : (
            hasMoreCursor && (
              <UserRelationshipMoreButton
                clickCode={moreButtonClickCode}
                type={type}
                onClickButtonMore={fetchMore}
                remains={remains}
              />
            )
          )}
        </>
      )}
    </>
  )
}

export default UserRelationshipList
